import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, User, Inbox, Plus, Trash2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  PRIORITY_COLORS,
  STATUS_COLUMNS,
  normalizeStatus,
  personChipClass,
  vendorChipClass,
  type Priority,
  type Status,
} from "./directory-data";
import { usePeople, useVendors } from "./use-directory";
import { useDeleteTask, type EditableTask } from "./use-tasks";
import { AddTaskDialog } from "./AddTaskDialog";
import { InlineAddTask } from "./InlineAddTask";
import { DirectoryComments } from "./DirectoryComments";

type TaggedRef = {
  id: number | string;
  name: string;
  slug: string;
  chip_color: string | null;
};

type RawOpsTask = {
  id: number | string;
  title: string;
  description: string | null;
  priority: string;
  status: string;
  category: string | null;
  due_date: string | null;
  people: TaggedRef[];
  vendors: TaggedRef[];
};

type Task = {
  id: number | string;
  title: string;
  description: string;
  vendors: TaggedRef[];
  people: TaggedRef[];
  priority: Priority;
  status: Status;
  category: string;
  dueDate?: string;
  raw: RawOpsTask;
};

export function DirectoryDetailView({
  entityType,
  entityName,
}: {
  entityType: "person" | "vendor";
  entityName: string;
}) {
  const api = useApi();
  const peopleQ = usePeople();
  const vendorsQ = useVendors();
  const deleteTask = useDeleteTask();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<EditableTask | null>(null);

  const hubPath = entityType === "person" ? "/people" : "/vendors";
  const hubLabel = entityType === "person" ? "People" : "Vendors";

  const entityId = useMemo(() => {
    if (entityType === "person") {
      return (peopleQ.data ?? []).find((p) => p.name === entityName)?.id;
    }
    return (vendorsQ.data ?? []).find((v) => v.name === entityName)?.id;
  }, [entityType, entityName, peopleQ.data, vendorsQ.data]);

  const inlineDefaults =
    entityType === "person"
      ? { person_ids: entityId ? [entityId] : [] }
      : { vendor_ids: entityId ? [entityId] : [] };
  const dialogDefaults =
    entityType === "person"
      ? { person_ids: entityId ? [entityId] : [] }
      : { vendor_ids: entityId ? [entityId] : [] };

  const { data, isLoading } = useQuery({
    queryKey: ["operations_tasks"],
    queryFn: async (): Promise<RawOpsTask[]> => {
      const res = await api(`/api/operations_tasks`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as RawOpsTask[];
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  const tasks: Task[] = useMemo(() => {
    if (!data) return [];
    return data
      .filter((row) => {
        if (entityType === "person")
          return (row.people ?? []).some((p) => p.name === entityName);
        return (row.vendors ?? []).some((v) => v.name === entityName);
      })
      .map((row) => ({
        id: row.id,
        title: row.title || "",
        description: row.description || "",
        vendors: row.vendors ?? [],
        people: row.people ?? [],
        priority: (row.priority as Priority) || "medium",
        status: normalizeStatus(row.status),
        category: row.category || "Operations",
        dueDate: row.due_date
          ? new Date(row.due_date).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })
          : undefined,
        raw: row,
      }));
  }, [data, entityType, entityName]);

  const tasksByStatus = useMemo(() => {
    const groups: Record<Status, Task[]> = {
      todo: [],
      in_progress: [],
      waiting: [],
      done: [],
    };
    for (const task of tasks) groups[task.status].push(task);
    return groups;
  }, [tasks]);

  const total = tasks.length;
  const open = total - tasksByStatus.done.length;

  function openEditFor(task: Task) {
    setEditingTask({
      id: task.id,
      title: task.title,
      description: task.description || null,
      status: task.status,
      priority: task.priority,
      category: task.category,
      due_date: task.raw.due_date,
      people: task.people.map((p) => ({ id: p.id, name: p.name })),
      vendors: task.vendors.map((v) => ({ id: v.id, name: v.name })),
    });
  }

  function confirmDelete(task: Task) {
    const ok = window.confirm(
      `Delete "${task.title || "untitled"}"?\n\nThis cannot be undone.`,
    );
    if (!ok) return;
    deleteTask.mutate(task.id);
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-4 border-b">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
          <Link
            to={hubPath}
            className="inline-flex items-center gap-1 hover:text-foreground"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {hubLabel}
          </Link>
          <span>/</span>
          <span className="text-foreground font-medium">{entityName}</span>
        </div>
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-foreground">{entityName}</h1>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">
              {isLoading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin inline" />
              ) : (
                `${open} open · ${total} total`
              )}
            </span>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="w-4 h-4 mr-1" />
              New task
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        <div className="flex flex-1 min-w-0 flex-col">
          {!isLoading && tasks.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-3">
              <Inbox className="w-10 h-10 opacity-50" />
              <div className="text-sm">
                No action items tagged for {entityName} yet.
              </div>
              <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)}>
                <Plus className="w-4 h-4 mr-1" />
                Add the first one
              </Button>
            </div>
          ) : (
            <div className="flex-1 overflow-auto bg-[#f8fafc] p-6">
              <div className="flex gap-4 h-full min-h-0 min-w-[900px]">
                {STATUS_COLUMNS.map((col) => {
                  const colTasks = tasksByStatus[col.id];
                  return (
                    <div key={col.id} className="flex flex-col flex-1 min-w-[220px]">
                      <div
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg mb-3 ${col.color}`}
                      >
                        <span className={`w-2 h-2 rounded-full ${col.dot}`} />
                        <span className="text-sm font-semibold">{col.label}</span>
                        <span className="ml-auto text-xs font-medium opacity-70">
                          {colTasks.length}
                        </span>
                      </div>

                      <div className="mb-2">
                        <InlineAddTask status={col.id} defaults={inlineDefaults} />
                      </div>

                      <div className="flex flex-col gap-3 overflow-y-auto flex-1">
                        {colTasks.map((task) => (
                          <div
                            key={String(task.id)}
                            role="button"
                            tabIndex={0}
                            onClick={() => openEditFor(task)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                openEditFor(task);
                              }
                            }}
                            className="bg-card border border-border rounded-xl p-4 hover:shadow-md transition-all cursor-pointer relative"
                          >
                            <button
                              type="button"
                              aria-label="Delete task"
                              title="Delete task"
                              onClick={(e) => {
                                e.stopPropagation();
                                confirmDelete(task);
                              }}
                              className="absolute top-2 right-2 p-1 rounded text-muted-foreground/60 hover:text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>

                            <div className="flex items-center gap-2 mb-2.5 pr-6">
                              <span
                                className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                                  PRIORITY_COLORS[task.priority] ?? "bg-slate-400"
                                }`}
                                title={task.priority}
                              />
                              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                                {task.category}
                              </span>
                            </div>

                            <p className="text-sm font-semibold text-foreground leading-snug mb-1.5">
                              {task.title}
                            </p>

                            {task.description && (
                              <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 mb-3">
                                {task.description}
                              </p>
                            )}

                            <div className="flex flex-wrap gap-1.5">
                              {task.vendors.map((v) => (
                                <span
                                  key={String(v.id)}
                                  className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${vendorChipClass(
                                    v.name,
                                    v.chip_color,
                                  )}`}
                                >
                                  {v.name}
                                </span>
                              ))}
                              {task.people.map((p) => (
                                <span
                                  key={String(p.id)}
                                  className={`inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded ${personChipClass(
                                    p.name,
                                    p.chip_color,
                                  )}`}
                                >
                                  <User className="w-2.5 h-2.5" />
                                  {p.name}
                                </span>
                              ))}
                              {task.dueDate && (
                                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-muted text-muted-foreground ml-auto">
                                  Due {task.dueDate}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <DirectoryComments
          entityType={entityType}
          entityId={entityId}
          entityName={entityName}
        />
      </div>

      <AddTaskDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        defaults={dialogDefaults}
      />

      <AddTaskDialog
        open={!!editingTask}
        onOpenChange={(open) => {
          if (!open) setEditingTask(null);
        }}
        task={editingTask ?? undefined}
      />
    </div>
  );
}
